import React from "react";

import Loading from "ui/Loading";
import Panel from "ui/Panel";
import { MatchRule, toMatchRuleInput } from "features/Server/ServerGroupForm";

import { useServerGroupMembersQuery } from "./gql/Query.generated";

const BASELINE_POLL_MS = 45_000;

type Props = {
  organizationSlug: string;
  serverGroupId?: string;
  matchRules: MatchRule[];
};

const PreviewDiff = ({
  organizationSlug,
  serverGroupId,
  matchRules,
}: Props) => {
  const baseline = useServerGroupMembersQuery({
    variables: { organizationSlug, serverGroupId },
    pollInterval: BASELINE_POLL_MS,
  });
  const preview = useServerGroupMembersQuery({
    variables: {
      organizationSlug,
      serverGroupId,
      matchRules: toMatchRuleInput(matchRules),
    },
  });

  const baselineMembers = baseline.data?.getServerGroupMembers;
  const previewMembers = preview.data?.getServerGroupMembers;
  const error = baseline.error ?? preview.error;
  const updating = baseline.loading || preview.loading;

  const baselineIds = new Set(
    (baselineMembers ?? []).map((member) => member.id),
  );
  const previewIds = new Set((previewMembers ?? []).map((member) => member.id));
  const added = (previewMembers ?? []).filter(
    (member) => !baselineIds.has(member.id),
  );
  const removed = (baselineMembers ?? []).filter(
    (member) => !previewIds.has(member.id),
  );
  const unchanged = (previewMembers ?? []).filter((member) =>
    baselineIds.has(member.id),
  );

  const renderBody = () => {
    if (error) {
      return <Loading error />;
    }
    if (!(baselineMembers !== undefined && previewMembers !== undefined)) {
      return <Loading />;
    }
    if (added.length === 0 && removed.length === 0 && unchanged.length === 0) {
      return <div className="text-[#606060]">No matching servers.</div>;
    }
    return (
      <ul className="list-none m-0 p-0">
        {removed.map((member) => (
          <li key={member.id} className="text-[#C22426]">
            - {member.name}
          </li>
        ))}
        {added.map((member) => (
          <li key={member.id} className="text-[#0F792B]">
            + {member.name}
          </li>
        ))}
        {unchanged.map((member) => (
          <li key={member.id} className="text-[#606060]">
            {member.name}
          </li>
        ))}
      </ul>
    );
  };

  const title =
    previewMembers !== undefined
      ? `Live Preview — ${previewMembers.length} matching servers`
      : "Live Preview";

  const secondaryTitle = updating ? (
    "Updating..."
  ) : (
    <>
      {added.length > 0 && (
        <span className="text-[#0F792B]">+{added.length}</span>
      )}
      {removed.length > 0 && (
        <span className="text-[#C22426] ml-2">−{removed.length}</span>
      )}
    </>
  );

  return (
    <Panel title={title} secondaryTitle={secondaryTitle}>
      <div
        className={`p-[10px] transition-opacity ${
          updating ? "opacity-50" : ""
        }`}
      >
        {renderBody()}
      </div>
    </Panel>
  );
};

export default PreviewDiff;
